import { TransitionKind, TransitionSpeed } from "@repo/api/transition";
import {
  getDefaultPreviewClipDurationsInFrames,
  ImageTransitionBuilder,
  ImageTransitionWrapperProps,
} from "./ImageTransitionBuilder.ts";
import { msToFrames } from "../../utils/timing.ts";
import { lightLeak } from "@remotion/effects/light-leak";
import { Easing, interpolate, Solid, useVideoConfig } from "remotion";

const transitionKind = TransitionKind.enum.LIGHT_LEAK;

export const LIGHT_LEAK_DEFAULT_INTENSITY = 0.7;
export const LIGHT_LEAK_DEFAULT_SEED = 1;

const LIGHT_LEAK_DURATION_MS: Record<TransitionSpeed, number> = {
  SLOW: 2600,
  NORMAL: 1800,
  FAST: 1100,
};

// Total leak length, centered on the cut between the two images.
export function getLightLeakDurationInFrames(
  speed: TransitionSpeed | undefined,
  fps: number,
) {
  return msToFrames(LIGHT_LEAK_DURATION_MS[speed ?? "NORMAL"], fps);
}

export const ImageLightLeakTransitionBuilder: ImageTransitionBuilder<
  Record<string, unknown>
> = {
  formConfig: {
    kind: transitionKind,
    buildDefaultValue: () => ({
      kind: transitionKind,
      speed: "NORMAL",
      intensity: LIGHT_LEAK_DEFAULT_INTENSITY,
      seed: LIGHT_LEAK_DEFAULT_SEED,
    }),
    speed: ["SLOW", "NORMAL", "FAST"],
    intensity: true,
    seed: true,
  },
  wrappers: {
    prevImageWrapper: {
      overlayComponent: PrevImageOverlay,
    },
    nextImageWrapper: {
      overlayComponent: NextImageOverlay,
    },
  },

  getPreviewClipDurationsInFrames: getDefaultPreviewClipDurationsInFrames,
};

const FADE_OUT_START = 0.45;
const LIGHT_LEAK_HUE_SHIFT = 15;

// The leak straddles the cut: its first half plays over the end of the previous image.
function PrevImageOverlay({
  imageTransition,
  fps,
  imageDurationInFrames,
  currentFrame,
}: ImageTransitionWrapperProps) {
  const durationInFrames = getLightLeakDurationInFrames(
    imageTransition.speed,
    fps,
  );
  const half = Math.min(
    Math.floor(durationInFrames / 2),
    imageDurationInFrames,
  );
  const startFrame = imageDurationInFrames - half;

  if (currentFrame < startFrame) {
    return null;
  }

  return (
    <LightLeakLayer
      frame={currentFrame - startFrame}
      durationInFrames={durationInFrames}
      intensity={imageTransition.intensity ?? LIGHT_LEAK_DEFAULT_INTENSITY}
      seed={imageTransition.seed ?? LIGHT_LEAK_DEFAULT_SEED}
    />
  );
}

function NextImageOverlay({
  imageTransition,
  fps,
  imageDurationInFrames,
  currentFrame,
}: ImageTransitionWrapperProps) {
  const durationInFrames = getLightLeakDurationInFrames(
    imageTransition.speed,
    fps,
  );
  const half = Math.floor(durationInFrames / 2);
  const endFrame = Math.min(durationInFrames - half, imageDurationInFrames);

  if (currentFrame >= endFrame) {
    return null;
  }

  return (
    <LightLeakLayer
      frame={half + currentFrame}
      durationInFrames={durationInFrames}
      intensity={imageTransition.intensity ?? LIGHT_LEAK_DEFAULT_INTENSITY}
      seed={imageTransition.seed ?? LIGHT_LEAK_DEFAULT_SEED}
    />
  );
}

function LightLeakLayer({
  frame,
  durationInFrames,
  intensity,
  seed,
}: {
  frame: number;
  durationInFrames: number;
  intensity: number;
  seed: number;
}) {
  const { width, height } = useVideoConfig();
  const lastFrame = Math.max(durationInFrames - 1, 1);

  const opacity = interpolate(
    frame,
    [lastFrame * FADE_OUT_START, lastFrame],
    [intensity, 0],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.out(Easing.quad),
    },
  );

  return (
    <Solid
      width={width}
      height={height}
      className="pointer-events-none absolute inset-0"
      style={{ opacity, mixBlendMode: "screen" }}
      effects={[
        lightLeak({
          seed,
          hueShift: LIGHT_LEAK_HUE_SHIFT,
          progress: interpolate(frame, [0, lastFrame], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
        }),
      ]}
    />
  );
}
